import React, { useRef } from "react";
import "./Testimonials.css";
import useRevealOnIntersect from "../hooks/useRevealOnIntersect";

const DATA = [
  {
    quote:
      "Nexite helped us scale our business faster than ever. Their tech is world‑class and the team is brilliant.",
    name: "Sarah Malik",
    title: "CEO",
    company: "TechNova",
    img: "/testimonials/user1.jpg", // drop your image at public/testimonials/user1.jpg
    since: "Since 2022",
    rating: 5,
  },
  {
    quote:
      "The team at Nexite is super professional and innovative. Delivery was on time and the outcomes measurable.",
    name: "Jamal R.",
    title: "Product Head",
    company: "Byteworks",
    img: "/testimonials/user2.jpg",
    since: "Since 2021",
    rating: 5,
  },
  {
    quote:
      "From design to rollout, Nexite made complex things simple. We saw fewer incidents and better performance.",
    name: "Ayesha Qureshi",
    title: "IT Director",
    company: "CloudMint",
    img: "/testimonials/user3.jpg",
    since: "Since 2020",
    rating: 5,
  },
  {
    quote:
      "Security and scale were our priorities. Nexite delivered both—without compromising usability for our teams.",
    name: "Omar Khan",
    title: "Head of Infrastructure",
    company: "DeltaSecure",
    img: "/testimonials/user4.jpg",
    since: "Since 2023",
    rating: 5,
  },
  {
    quote:
      "Observability insights were a game changer. Their approach saved us time and reduced costs significantly.",
    name: "Zara A.",
    title: "Operations Lead",
    company: "FiberGrid",
    img: "/testimonials/user5.jpg",
    since: "Since 2022",
    rating: 5,
  },
  {
    quote:
      "Elegant solutions, clear communication, and real results. We’d recommend Nexite without hesitation.",
    name: "Ahmed J.",
    title: "CTO",
    company: "VisionAI",
    img: "/testimonials/user6.jpg",
    since: "Since 2021",
    rating: 5,
  },
];

function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "").concat(parts[1]?.[0] || "").toUpperCase();
}

function Stars({ count = 5 }) {
  return (
    <span className="ts-stars" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          className={`star ${i < count ? "filled" : ""}`}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.786 1.4 8.164L12 18.897l-7.334 3.863 1.4-8.164L.132 9.21l8.2-1.192L12 .587z" />
        </svg>
      ))}
    </span>
  );
}

export default function Testimonials() {
  const scrollerRef = useRef(null);

  useRevealOnIntersect(".ts-reveal", { threshold: 0.2 });

  const scrollByCards = (dir = 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector(".ts-card");
    const amount = card ? card.clientWidth + 16 : 320;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section id="testimonials" className="testimonials-section">
      <div className="ts-hero-bg" aria-hidden="true">
        <span className="ts-blob b1" />
        <span className="ts-blob b2" />
        <span className="ts-blob b3" />
        <span className="ts-grid-glow" />
      </div>

      <div className="ts-container">
        <header className="ts-header ts-reveal" data-reveal="up" style={{ "--d": "40ms" }}>
          <span className="ts-pill">What Our Clients Say</span>
          <h2>Real outcomes. Happy teams.</h2>
          <p className="ts-subtitle">
            Trusted by modern organizations for secure, scalable, and elegant solutions.
          </p>
        </header>

        <div className="ts-summary ts-reveal" data-reveal="up" style={{ "--d": "90ms" }}>
          <div className="ts-summary-card">
            <strong>Long-term delivery relationships</strong>
            <span>Clients return for rollout support, expansion work, and operational guidance after implementation.</span>
          </div>
          <div className="ts-summary-metrics" aria-label="Client results overview">
            <div><strong>99.9%</strong><span>uptime targets supported</span></div>
            <div><strong>50+</strong><span>projects delivered</span></div>
            <div><strong>24/7</strong><span>support mindset</span></div>
          </div>
        </div>

        {/* Arrow controls (visible on mobile/tablet) */}
        <div className="ts-controls">
          <button className="ts-nav prev" aria-label="Previous" onClick={() => scrollByCards(-1)}>
            ‹
          </button>
          <button className="ts-nav next" aria-label="Next" onClick={() => scrollByCards(1)}>
            ›
          </button>
        </div>

        <div className="ts-scroller" ref={scrollerRef}>
          {DATA.map((t, i) => (
            <article
              key={`${t.name}-${i}`}
              className="ts-card ts-reveal"
              data-reveal="up"
              style={{ "--d": `${60 + i * 80}ms` }}
            >
              <div className="ts-quote-mark" aria-hidden="true">“</div>

              {/* Avatar with dummy image slot that looks superb by default */}
              <div className="ts-head">
                <div className="ts-avatar">
                  {t.img ? (
                    <img
                      src={t.img}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.parentElement?.querySelector(".ts-fallback");
                        if (fallback) fallback.style.display = "grid";
                      }}
                    />
                  ) : null}
                  <div className="ts-fallback" aria-hidden="true">
                    <span>{initialsOf(t.name)}</span>
                  </div>
                  <span className="ts-avatar-ring" aria-hidden="true" />
                </div>
                <div className="ts-meta">
                  <div className="ts-name">{t.name}</div>
                  <div className="ts-role">
                    {t.title} · {t.company}
                  </div>
                  <div className="ts-since">{t.since}</div>
                </div>
                <Stars count={t.rating} />
              </div>

              <blockquote className="ts-quote">
                <p>{t.quote}</p>
              </blockquote>

              <div className="ts-footer-row">
                <span className="ts-company-chip">{t.company}</span>
                <span className="ts-role-chip">{t.title}</span>
              </div>

              <span className="card-shine" aria-hidden="true" />
            </article>
          ))}
        </div>
      </div>

      <div className="ts-accent" aria-hidden="true" />
    </section>
  );
}